/**
 * ============================================================================
 * orgHooks.ts — TanStack Query hooks for the department hierarchy + SOP
 *               template library (V18).
 *
 * Owner: Phase 7+ — Department hierarchy model.
 * URS rows: WF-031..038.
 * ============================================================================
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from './client';

export interface Department {
  departmentId: string;
  parentId: string | null;
  code: string;
  name: string;
  siteCode: string | null;
  costCenter: string | null;
  isActive: boolean;
}

export interface DepartmentPosition {
  positionId: string;
  departmentId: string;
  positionKind:
    | 'DEPARTMENT_HEAD' | 'DEPUTY_HEAD' | 'QA_MANAGER'
    | 'AUTHOR' | 'REVIEWER' | 'DOC_CONTROLLER' | 'TRAINEE';
  userId: string;
  userName: string;
  backupUserId: string | null;
  backupUserName: string | null;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
}

export interface ReviewerChainItem {
  positionKind: 'DEPARTMENT_HEAD' | 'QA_MANAGER';
  userId: string;
  userName: string;
  backupUserId: string | null;
  backupUserName: string | null;
}

export interface SopTemplate {
  templateId: string;
  code: string;
  title: string;
  docTypeCode: string;
  owningDepartmentId: string | null;
  owningDepartmentName: string | null;
  versionMajor: number;
  versionMinor: number;
  status: 'DRAFT' | 'IN_REVIEW' | 'APPROVED' | 'WITHDRAWN';
  artifactRef: string | null;
  artifactMime: string | null;
  artifactSizeBytes: number | null;
  description: string | null;
  approvedAtUtc: string | null;
  approvedByUserId: string | null;
}

export function useDepartments(): UseQueryResult<{ items: Department[] }> {
  return useQuery({
    queryKey: ['org', 'departments'],
    queryFn: () => apiFetch<{ items: Department[] }>('/api/v1/departments'),
    staleTime: 5 * 60_000,
  });
}

export function useDepartmentPositions(deptId: string | undefined): UseQueryResult<{ items: DepartmentPosition[] }> {
  return useQuery({
    queryKey: ['org', 'departments', deptId ?? '', 'positions'],
    queryFn: () => apiFetch<{ items: DepartmentPosition[] }>(`/api/v1/departments/${deptId}/positions`),
    enabled: !!deptId,
    staleTime: 5 * 60_000,
  });
}

export function useReviewerChain(deptId: string | undefined): UseQueryResult<{ chain: ReviewerChainItem[] }> {
  return useQuery({
    queryKey: ['org', 'departments', deptId ?? '', 'reviewers'],
    queryFn: () => apiFetch<{ chain: ReviewerChainItem[] }>(`/api/v1/departments/${deptId}/reviewers`),
    enabled: !!deptId,
    staleTime: 60_000,
  });
}

export function useSopTemplates(docTypeCode?: string): UseQueryResult<{ items: SopTemplate[] }> {
  return useQuery({
    queryKey: ['org', 'sop-templates', docTypeCode ?? 'all'],
    queryFn: () => apiFetch<{ items: SopTemplate[] }>(
      `/api/v1/sop-templates${docTypeCode ? `?docType=${encodeURIComponent(docTypeCode)}` : ''}`,
    ),
    staleTime: 5 * 60_000,
  });
}

// ============================================================================
// Mutation hooks — V18 admin mutations (item A)
// ============================================================================

export interface CreateDepartmentInput {
  code: string;
  name: string;
  parentId?: string | null;
  siteCode?: string;
  costCenter?: string;
}
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDepartmentInput) =>
      apiFetch<Department>('/api/v1/departments', { method: 'POST', body: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org', 'departments'] }); },
  });
}

export interface UpdateDepartmentInput {
  name?: string;
  parentId?: string | null;
  siteCode?: string | null;
  costCenter?: string | null;
  isActive?: boolean;
}
export function useUpdateDepartment(deptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDepartmentInput) =>
      apiFetch<Department>(`/api/v1/departments/${deptId}`, { method: 'PUT', body: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org', 'departments'] }); },
  });
}

export interface CreatePositionInput {
  positionKind: DepartmentPosition['positionKind'];
  userId: string;
  userName: string;
  backupUserId?: string;
  backupUserName?: string;
  notes?: string;
}
export function useRotatePosition(deptId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePositionInput) =>
      apiFetch<DepartmentPosition>(`/api/v1/departments/${deptId}/positions`, { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', 'departments', deptId, 'positions'] });
      qc.invalidateQueries({ queryKey: ['org', 'departments', deptId, 'reviewers'] });
    },
  });
}

export interface CreateTemplateInput {
  code: string;
  title: string;
  docTypeCode: string;
  owningDepartmentId?: string;
  description?: string;
  artifactBase64?: string;
  artifactMime?: string;
  artifactFilename?: string;
}
export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateInput) =>
      apiFetch<SopTemplate>('/api/v1/sop-templates', { method: 'POST', body: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org', 'sop-templates'] }); },
  });
}

export function useTransitionTemplate(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { to: 'IN_REVIEW' | 'APPROVED' | 'WITHDRAWN' | 'DRAFT'; signerUserId?: string }) =>
      apiFetch<SopTemplate>(`/api/v1/sop-templates/${templateId}/transition`, { method: 'POST', body: input }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org', 'sop-templates'] }); },
  });
}

// C-11 — clone an existing template into a new DRAFT (copies metadata + artefact ref).
export function useCloneTemplate(templateId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<SopTemplate>(`/api/v1/sop-templates/${templateId}/clone`, { method: 'POST', body: {} }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['org', 'sop-templates'] }); },
  });
}

// ============================================================================
// Document → quality-event back-links (item D)
// ============================================================================

export interface QualityEventLink {
  kind: 'CAPA' | 'DEVIATION' | 'OOS';
  id: string;
  number: string;
  title: string;
  status: string;
  criticality?: string;
  classification?: string;
  link: string;
}
export function useDocumentQualityEvents(docId: string | undefined): UseQueryResult<{ items: QualityEventLink[] }> {
  return useQuery({
    queryKey: ['documents', docId ?? '', 'quality-events'],
    queryFn: () => apiFetch<{ items: QualityEventLink[] }>(`/api/v1/documents/${docId}/quality-events`),
    enabled: !!docId,
    staleTime: 30_000,
  });
}

// ============================================================================
// Multi-site registry (V26) — Sites + Site Authorization Matrix + Effectivity
// ============================================================================

export interface Site {
  site_id: string;
  site_code: string;
  name: string;
  city: string | null;
  country_code: string | null;
  timezone: string;
  site_kind:
    | 'MANUFACTURING_HQ'
    | 'MANUFACTURING'
    | 'PACKAGING'
    | 'WAREHOUSE'
    | 'LAB_QC'
    | 'HEAD_OFFICE'
    | 'DISTRIBUTION';
  is_active: boolean;
  gxp_in_scope: boolean;
  license_ref: string | null;
  license_expiry: string | null;
  quality_head_user_id: string | null;
  notes: string | null;
}

export function useSites(): UseQueryResult<{ items: Site[]; total: number }> {
  return useQuery({
    queryKey: ['org', 'sites'],
    queryFn: () => apiFetch<{ items: Site[]; total: number }>('/api/v1/sites'),
    staleTime: 5 * 60_000,
  });
}

export interface SiteAuthMatrixEntry {
  matrix_id: string;
  site_id: string;
  site_code: string;
  site_name: string;
  action_code: string;
  required_role: string;
  dual_sign_required: boolean;
  secondary_role: string | null;
  effective_from_utc: string;
  notes: string | null;
}

export function useSiteAuthMatrix(): UseQueryResult<{ items: SiteAuthMatrixEntry[]; total: number }> {
  return useQuery({
    queryKey: ['org', 'site-authorization-matrix'],
    queryFn: () =>
      apiFetch<{ items: SiteAuthMatrixEntry[]; total: number }>('/api/v1/site-authorization-matrix'),
    staleTime: 5 * 60_000,
  });
}

export interface DocSiteEffectivity {
  effectivity_id: string;
  site_id: string;
  site_code: string;
  site_name: string;
  effective_at_utc: string | null;
  status: 'PENDING' | 'EFFECTIVE' | 'SUPERSEDED' | 'WITHDRAWN' | 'NOT_APPLICABLE';
  pending_reason: string | null;
  appendix_summary: string | null;
  activated_by_user_id: string | null;
  created_at_utc: string;
}

export function useDocSiteEffectivity(
  docId: string | undefined
): UseQueryResult<{ items: DocSiteEffectivity[]; total: number }> {
  return useQuery({
    queryKey: ['documents', docId ?? '', 'site-effectivity'],
    queryFn: () =>
      apiFetch<{ items: DocSiteEffectivity[]; total: number }>(
        `/api/v1/documents/${docId}/site-effectivity`
      ),
    enabled: !!docId,
    staleTime: 60_000,
  });
}

export interface TrainingMatrixEntry {
  matrix_id: string;
  site_id: string;
  site_code: string;
  site_name: string;
  role_code: string;
  doc_type_code: string;
  required: boolean;
  grace_days: number;
  requires_quiz: boolean;
  requires_practical: boolean;
}

export function useTrainingMatrix(): UseQueryResult<{ items: TrainingMatrixEntry[]; total: number }> {
  return useQuery({
    queryKey: ['training', 'matrix'],
    queryFn: () =>
      apiFetch<{ items: TrainingMatrixEntry[]; total: number }>('/api/v1/training/matrix'),
    staleTime: 5 * 60_000,
  });
}

export interface SiteKpis {
  site_id: string;
  site_code: string;
  site_name: string;
  city: string | null;
  site_kind: Site['site_kind'];
  license_ref: string | null;
  license_expiry: string | null;
  days_to_license_expiry: number | null;
  kpis: {
    capa_open: number;
    capa_overdue: number;
    deviation_critical_major: number;
    oos_open: number;
    training_overdue: number;
    training_overdue_pct: number;
    suppliers_active: number;
    equipment_qualified: number;
    equipment_restricted: number;
  };
}

export function useSiteKpis(): UseQueryResult<{ items: SiteKpis[] }> {
  return useQuery({
    queryKey: ['sites', 'kpis'],
    queryFn: () => apiFetch<{ items: SiteKpis[] }>('/api/v1/sites/kpis'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export interface DepartmentKpis {
  department_id: string;
  code: string;
  name: string;
  site_id: string | null;
  site_code: string | null;
  cost_center: string | null;
  head: {
    user_id: string;
    name: string;
    initials: string;
    title: string;
  } | null;
  kpis: {
    documents_active: number;
    training_overdue: number;
    training_overdue_pct: number;
    capa_open: number;
    capa_overdue: number;
    deviation_critical_major: number;
    audit_findings_open: number;
    heat_score: number;
  };
}

export function useDepartmentKpis(): UseQueryResult<{ items: DepartmentKpis[] }> {
  return useQuery({
    queryKey: ['departments', 'kpis'],
    queryFn: () => apiFetch<{ items: DepartmentKpis[] }>('/api/v1/departments/kpis'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export interface DailyBriefing {
  generated_at_utc: string;
  briefing: string;
  attention_items: string[];
  source: string;
  advisory_only: boolean;
  facts?: Record<string, unknown>;
}

export function useDailyBriefing(): UseQueryResult<DailyBriefing> {
  return useQuery({
    queryKey: ['ai', 'daily-briefing'],
    queryFn: () => apiFetch<DailyBriefing>('/api/v1/ai/daily-briefing'),
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });
}

export interface PendingSignature {
  kind: string;
  id: string;
  label: string;
  hint: string;
  link: string;
  urgency: 'high' | 'medium' | 'low';
}

export function usePendingSignatures(): UseQueryResult<{ items: PendingSignature[] }> {
  return useQuery({
    queryKey: ['signatures', 'pending'],
    queryFn: () => apiFetch<{ items: PendingSignature[] }>('/api/v1/signatures/pending'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export interface GxpDeadline {
  kind: 'SITE_LICENSE' | 'EQUIPMENT_REQUAL' | 'EQUIPMENT_CAL' | 'SUPPLIER_REQUAL' | 'AUDIT';
  ref: string;
  title: string;
  due_date: string;
  days_until_due: number;
  detail: string | null;
  site_id: string | null;
  urgency: 'overdue' | 'high' | 'medium' | 'low';
}

export function useGxpDeadlines(): UseQueryResult<{ items: GxpDeadline[] }> {
  return useQuery({
    queryKey: ['gxp-deadlines'],
    queryFn: () => apiFetch<{ items: GxpDeadline[] }>('/api/v1/gxp-deadlines'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export interface ComplianceHealthScore {
  score: number | null;
  band: 'green' | 'amber' | 'red';
  label: string;
  generated_at_utc: string;
  axes: { axis: string; deduction: number; max_deduction: number; health: number }[];
}

export function useComplianceHealthScore(): UseQueryResult<ComplianceHealthScore> {
  return useQuery({
    queryKey: ['compliance-health-score'],
    queryFn: () => apiFetch<ComplianceHealthScore>('/api/v1/compliance-health-score'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

// ============================================================================
// Home v2 — activity feed / KPI trends / risk heatmap (v0.12.5)
// ============================================================================

export interface ActivityFeedItem {
  kind: 'CAPA' | 'DEVIATION' | 'OOS' | 'AUDIT_FINDING';
  id: string;
  ref: string;
  title: string;
  severity: string | null;
  status: string;
  at: string;
}

export function useActivityFeed(): UseQueryResult<{ items: ActivityFeedItem[] }> {
  return useQuery({
    queryKey: ['home', 'activity-feed'],
    queryFn: () => apiFetch<{ items: ActivityFeedItem[] }>('/api/v1/home/activity-feed'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export interface KpiTrendSeries {
  current: number;
  history: { week: number; value: number }[];
}

export interface KpiTrends {
  capa_open: KpiTrendSeries;
  dev_critical_major: KpiTrendSeries;
  oos_open: KpiTrendSeries;
  training_overdue: KpiTrendSeries;
  audit_open: KpiTrendSeries;
  generated_at_utc: string;
}

export function useKpiTrends(): UseQueryResult<KpiTrends> {
  return useQuery({
    queryKey: ['home', 'kpi-trends'],
    queryFn: () => apiFetch<KpiTrends>('/api/v1/home/kpi-trends'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

export interface RiskHeatmap {
  sites: string[];
  severities: ('CRITICAL' | 'MAJOR' | 'MINOR')[];
  matrix: Record<string, Record<string, number | string>>;
}

export function useRiskHeatmap(): UseQueryResult<RiskHeatmap> {
  return useQuery({
    queryKey: ['home', 'risk-heatmap'],
    queryFn: () => apiFetch<RiskHeatmap>('/api/v1/home/risk-heatmap'),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}
